import { Link } from "react-router-dom";
import { Info } from "lucide-react";

import { useTranslation } from "@/i18n";
import { Alert } from "@/components/ui/alert";
import { CodeBlock } from "@/components/ui/code-block";
import { Section } from "@/components/shared/section";
import { ROUTES } from "@/lib/constants";

/**
 * Resolve the gateway base URL for display. Prefer the explicit
 * ``VITE_API_URL`` so the curl snippet copy-pastes cleanly. When
 * unset, fall back to ``localhost:8000`` in dev (Vite proxy
 * target) and to an obvious placeholder in a production build so
 * a forgotten env var never leaks "localhost" to a customer.
 */
function resolveGatewayBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_URL ?? "").trim();
  if (configured) return configured.replace(/\/+$/, "");
  if (import.meta.env.DEV) return "http://localhost:8000";
  return "https://YOUR_MONAGO_GATEWAY_URL";
}

const OPENAI_CURL = (base: string) => `curl -X POST ${base}/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_MONAGO_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-5.4-mini",
    "messages": [{"role": "user", "content": "Halo!"}]
  }'`;

const ANTHROPIC_CURL = (base: string) => `curl -X POST ${base}/v1/messages \\
  -H "Authorization: Bearer YOUR_MONAGO_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "claude-haiku-4-5",
    "messages": [{"role": "user", "content": "Halo!"}],
    "max_tokens": 1024
  }'`;

/**
 * "Cara akses API" section rendered above the API Keys table.
 *
 * Procurement-friendly developer onboarding: shows the gateway
 * base URL plus copy-paste curl snippets for the two providers
 * that ship adapter wiring at pilot scope (OpenAI + Anthropic).
 * Bedrock support lands at Sesi F.1b; no curl sample yet to
 * avoid implying readiness.
 */
export function ApiAccessSection() {
  const { t } = useTranslation();
  const baseUrl = resolveGatewayBaseUrl();

  return (
    <Section
      title={t("apiKeys.access.sectionTitle")}
      description={t("apiKeys.access.sectionDescription")}
    >
      <div className="space-y-4 rounded-lg border border-ocean-100 bg-white p-5">
        <div>
          <div className="label-eyebrow mb-1">
            {t("apiKeys.access.baseUrlLabel")}
          </div>
          <CodeBlock copyValue={baseUrl}>{baseUrl}</CodeBlock>
          <p className="mt-1 text-xs text-neutral-600">
            {t("apiKeys.access.baseUrlNote")}
          </p>
        </div>

        <div>
          <div className="label-eyebrow mb-1">
            {t("apiKeys.access.exampleOpenai")}{" "}
            <code className="text-ocean-700">gpt-5.4-mini</code>
          </div>
          <CodeBlock language="bash" copyValue={OPENAI_CURL(baseUrl)}>
            {OPENAI_CURL(baseUrl)}
          </CodeBlock>
        </div>

        <div>
          <div className="label-eyebrow mb-1">
            {t("apiKeys.access.exampleAnthropic")}{" "}
            <code className="text-ocean-700">claude-haiku-4-5</code>
          </div>
          <CodeBlock language="bash" copyValue={ANTHROPIC_CURL(baseUrl)}>
            {ANTHROPIC_CURL(baseUrl)}
          </CodeBlock>
        </div>

        <Alert variant="info">
          <div className="flex items-start gap-2 text-xs">
            <Info size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>{t("apiKeys.access.tipLabel")}</strong>{" "}
              {t("apiKeys.access.tipPrefix")}
              <Link
                to={ROUTES.PROVIDERS}
                className="font-medium text-ocean-700 underline"
              >
                {t("apiKeys.access.tipLinkLabel")}
              </Link>
              {t("apiKeys.access.tipSuffix")}
            </div>
          </div>
        </Alert>
      </div>
    </Section>
  );
}
